import {
    MarketBacklinks,
    MarketCompetitor,
    MarketData,
    MarketIntent,
    MarketKeyword,
    MarketKeywordGap,
    MarketOverview,
    MarketSeriesPoint,
} from '../../../common/hooks/use-market-api.hook';
import { cleanDomain, rankedCompetitors, seriesOf } from './market-presentation';

/**
 * The arithmetic behind the Business pages.
 *
 * Pure over the market read, so each claim a page makes ("869 searches sit on
 * page two", "the easiest search to win is…") is a function with a test
 * rather than a guess in a component. Every input is optional: snapshots
 * bought before a field existed are served as they were stored.
 */

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const n = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const has = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/* ─────────────────────────────── rankings ─────────────────────────────── */

export type BandKey = 'p1' | 'p2_3' | 'p4_10' | 'p11_20' | 'p21_50' | 'p51_100' | 'top3' | 'beyond';

export interface Band {
    key: BandKey;
    value: number;
}

/**
 * Where the rankings sit.
 *
 * Six bands when the snapshot has the full distribution; otherwise the three
 * an older snapshot can still honestly split into (top 3, rest of page one,
 * beyond). `exact` says which, so the page can say less rather than invent.
 */
export function rankingBands(o: MarketOverview | null | undefined): { exact: boolean; bands: Band[] } | null {
    if (!o) return null;
    const p = o.positions;
    if (p && Object.values(p).some(has)) {
        const bands: Band[] = (['p1', 'p2_3', 'p4_10', 'p11_20', 'p21_50', 'p51_100'] as const).map((key) => ({ key, value: n(p[key]) }));
        return bands.some((b) => b.value > 0) ? { exact: true, bands } : null;
    }
    const top3 = n(o.top3);
    const top10 = n(o.top10);
    const total = n(o.organicKeywords ?? o.top100);
    if (!total && !top10) return null;
    return {
        exact: false,
        bands: [
            { key: 'top3', value: top3 },
            { key: 'p4_10', value: Math.max(0, top10 - top3) },
            { key: 'beyond', value: Math.max(0, total - top10) },
        ],
    };
}

/** Page two, from the distribution when it is there. */
export const pageTwoCount = (o: MarketOverview | null | undefined): number | null =>
    has(o?.positions?.p11_20) ? (o!.positions!.p11_20 as number) : null;

/* ─────────────────────────────── keywords ─────────────────────────────── */

export type KeywordSegment = 'all' | 'top3' | 'page1' | 'quick' | 'up' | 'down' | 'new';
export const KEYWORD_SEGMENTS: KeywordSegment[] = ['all', 'top3', 'page1', 'quick', 'up', 'down', 'new'];

export function inSegment(k: MarketKeyword, segment: KeywordSegment): boolean {
    const pos = k.position;
    switch (segment) {
        case 'all':
            return true;
        case 'top3':
            return has(pos) && pos <= 3;
        case 'page1':
            return has(pos) && pos <= 10;
        case 'quick':
            return has(pos) && pos >= 11 && pos <= 20;
        case 'up':
            return k.movement === 'up';
        case 'down':
            return k.movement === 'down';
        case 'new':
            return k.movement === 'new';
    }
}

export function segmentCounts(keywords: MarketKeyword[]): Record<KeywordSegment, number> {
    const out = Object.fromEntries(KEYWORD_SEGMENTS.map((s) => [s, 0])) as Record<KeywordSegment, number>;
    for (const k of keywords) for (const s of KEYWORD_SEGMENTS) if (inSegment(k, s)) out[s]++;
    return out;
}

/** Whether any keyword carries movement at all — older snapshots do not. */
export const hasMovement = (keywords: MarketKeyword[]) => keywords.some((k) => !!k.movement);

/**
 * How much a search is worth chasing, from how many people make it: 0–1 on a
 * log scale where 100,000 searches a month is the top. Log, because the
 * difference between 50 and 500 searches matters as much as 5,000 to 50,000.
 */
export const volumeWeight = (volume: number | null | undefined): number => clamp(Math.log10(n(volume) + 1) / 5, 0, 1);

/** 0–1, higher is easier; an unknown difficulty is taken as middling. */
export const easeOf = (difficulty: number | null | undefined): number =>
    has(difficulty) ? 1 - clamp(difficulty, 0, 100) / 100 : 0.5;

/**
 * A page-two ranking's worth moving: volume × ease × how close it already is.
 * Position 11 is one place from page one; 20 is ten.
 */
export function quickWinScore(k: MarketKeyword): number {
    const pos = k.position;
    if (!has(pos) || pos < 11 || pos > 20) return 0;
    const closeness = 1 - ((pos - 11) / 9) * 0.5;
    return Math.round(100 * volumeWeight(k.searchVolume) * (0.4 + 0.6 * easeOf(k.difficulty)) * closeness);
}

/** Page-two rankings, the best to move first. */
export const quickWins = (keywords: MarketKeyword[], limit = 5): MarketKeyword[] =>
    keywords
        .filter((k) => inSegment(k, 'quick'))
        .sort((a, b) => quickWinScore(b) - quickWinScore(a) || n(b.searchVolume) - n(a.searchVolume))
        .slice(0, limit);

/** On page one but under the top three, biggest search first — the next place clicks are won. */
export const nearTop = (keywords: MarketKeyword[], limit = 5): MarketKeyword[] =>
    keywords
        .filter((k) => has(k.position) && k.position >= 4 && k.position <= 10)
        .sort((a, b) => n(b.searchVolume) - n(a.searchVolume))
        .slice(0, limit);

/** The searches bringing the most visits. */
export const bestKeywords = (keywords: MarketKeyword[], limit = 5): MarketKeyword[] =>
    [...keywords].sort((a, b) => n(b.estimatedVisits) - n(a.estimatedVisits) || n(b.searchVolume) - n(a.searchVolume)).slice(0, limit);

/** Places gained since the previous look (positive is better). Null without both positions. */
export const placesGained = (k: MarketKeyword): number | null =>
    has(k.previousPosition) && has(k.position) ? k.previousPosition - k.position : null;

/** The single biggest climb and fall among the keywords, for a sentence each. */
export function biggestMoves(keywords: MarketKeyword[]): { gain: MarketKeyword | null; loss: MarketKeyword | null } {
    let gain: MarketKeyword | null = null;
    let loss: MarketKeyword | null = null;
    let best = 0;
    let worst = 0;
    for (const k of keywords) {
        const d = placesGained(k);
        if (d == null || d === 0) continue;
        // Weighted by volume: rising twenty places on a search nobody makes is not news.
        const weight = d * (0.5 + volumeWeight(k.searchVolume));
        if (weight > best) {
            best = weight;
            gain = k;
        }
        if (weight < worst) {
            worst = weight;
            loss = k;
        }
    }
    return { gain, loss };
}

export type IntentKey = MarketIntent | 'unknown';
export const INTENTS: IntentKey[] = ['transactional', 'commercial', 'informational', 'navigational', 'unknown'];

/** How the searches split by what the searcher wants, by count or by the visits they bring. */
export function intentSplit(keywords: MarketKeyword[], by: 'count' | 'visits' = 'count'): { key: IntentKey; value: number }[] {
    const totals = new Map<IntentKey, number>(INTENTS.map((i) => [i, 0]));
    for (const k of keywords) {
        const key: IntentKey = k.intent && totals.has(k.intent) ? k.intent : 'unknown';
        totals.set(key, (totals.get(key) ?? 0) + (by === 'count' ? 1 : n(k.estimatedVisits)));
    }
    return INTENTS.map((key) => ({ key, value: totals.get(key) ?? 0 })).filter((s) => s.key !== 'unknown' || s.value > 0);
}

/** Share (0–1) of the visits that come from people looking to buy. Null when no intent is known. */
export function buyingShare(keywords: MarketKeyword[]): number | null {
    const split = intentSplit(keywords, 'visits');
    const known = split.filter((s) => s.key !== 'unknown').reduce((s, x) => s + x.value, 0);
    if (!known) return null;
    const buying = split.filter((s) => s.key === 'transactional' || s.key === 'commercial').reduce((s, x) => s + x.value, 0);
    return buying / known;
}

export interface LandingPage {
    url: string;
    fullUrl: string | null;
    title: string | null;
    visits: number;
    value: number;
    keywords: number;
    top: MarketKeyword;
}

/** Which pages bring the search traffic, and on which search each leans most. */
export function landingPages(keywords: MarketKeyword[], limit = 8): LandingPage[] {
    const pages = new Map<string, LandingPage>();
    for (const k of keywords) {
        const url = k.url || k.fullUrl;
        if (!url) continue;
        const page = pages.get(url) ?? { url, fullUrl: k.fullUrl ?? null, title: k.title ?? null, visits: 0, value: 0, keywords: 0, top: k };
        page.visits += n(k.estimatedVisits);
        page.value += n(k.trafficValue);
        page.keywords += 1;
        if (n(k.estimatedVisits) > n(page.top.estimatedVisits)) page.top = k;
        pages.set(url, page);
    }
    return [...pages.values()].sort((a, b) => b.visits - a.visits || b.keywords - a.keywords).slice(0, limit);
}

/** Searches whose demand fell hard over the year, among those bringing visits. */
export const fallingDemand = (keywords: MarketKeyword[], threshold = -25): MarketKeyword[] =>
    bestKeywords(keywords, 20).filter((k) => has(k.yearlyChange) && k.yearlyChange <= threshold && n(k.estimatedVisits) > 0);

/* ─────────────────────────────── history ─────────────────────────────── */

/** The newest value and the one before it, for a delta. Null without two points. */
export function historyChange(points: MarketSeriesPoint[] | undefined, key: string): { current: number; previous: number; since: string } | null {
    const s = seriesOf(points, key);
    if (s.length < 2) return null;
    const last = s[s.length - 1];
    const prev = s[s.length - 2];
    return { current: last.value, previous: prev.value, since: prev.month };
}

export const historyTrend = (points: MarketSeriesPoint[] | undefined, key: string): number[] => seriesOf(points, key).map((p) => p.value);

/** Percent change, or null from a zero baseline. */
export const pctChange = (current: number, previous: number): number | null => (previous ? ((current - previous) / previous) * 100 : null);

/* ─────────────────────────────── authority ─────────────────────────────── */

/**
 * DataForSEO's domain rank, read as authority out of 100.
 *
 * The backlinks summary is bought on its default 0–1000 scale; a person reads
 * scores out of a hundred, so it is divided by ten here and nowhere else.
 */
export const authorityScore = (rank: number | null | undefined): number | null => (has(rank) ? Math.round(clamp(rank, 0, 1000) / 10) : null);

export type AuthorityLevel = 'low' | 'modest' | 'solid' | 'strong';
export const authorityLevel = (score: number | null): AuthorityLevel | null =>
    score == null ? null : score < 20 ? 'low' : score < 40 ? 'modest' : score < 60 ? 'solid' : 'strong';

export type SpamLevel = 'low' | 'moderate' | 'high';
export const spamLevel = (score: number | null | undefined): SpamLevel | null =>
    !has(score) ? null : score < 30 ? 'low' : score < 60 ? 'moderate' : 'high';

/** Referring domains that pass authority (followed) against those that mostly do not. */
export function followSplit(b: MarketBacklinks | null | undefined): { followed: number; nofollow: number } | null {
    if (!b || !has(b.referringDomains) || !has(b.nofollowDomains)) return null;
    const nofollow = Math.min(b.nofollowDomains, b.referringDomains);
    return { followed: Math.max(0, b.referringDomains - nofollow), nofollow };
}

/**
 * How many independent voices the links are: linking sites on distinct
 * networks over linking sites. Near 1 is healthy; low means many of the sites
 * sit on the same servers, which search counts as fewer votes.
 */
export const linkDiversity = (b: MarketBacklinks | null | undefined): number | null =>
    b && has(b.referringSubnets) && n(b.referringDomains) > 0 ? clamp(b.referringSubnets / (b.referringDomains as number), 0, 1) : null;

/* ─────────────────────────────── competitors ─────────────────────────────── */

export interface You {
    domain: string;
    keywords: number;
    visits: number;
    top10: number;
    trafficValue: number | null;
}

export const youOf = (data: Pick<MarketData, 'target' | 'overview'>): You => ({
    domain: cleanDomain(data.target),
    keywords: n(data.overview?.organicKeywords),
    visits: n(data.overview?.estimatedMonthlyVisits),
    top10: n(data.overview?.top10),
    trafficValue: has(data.overview?.trafficValue) ? (data.overview!.trafficValue as number) : null,
});

/** Theirs over yours; null when either side is zero, since a ratio from nothing says nothing. */
export const ratioOf = (theirs: number | null | undefined, mine: number | null | undefined): number | null =>
    n(theirs) > 0 && n(mine) > 0 ? n(theirs) / n(mine) : null;

/** The rival with the most searches in common — the one most directly in the way. */
export const closestRival = (data: Pick<MarketData, 'competitors'>): MarketCompetitor | null => rankedCompetitors(data)[0] ?? null;

/** The rival found for the most searches overall. */
export const biggestRival = (data: Pick<MarketData, 'competitors'>): MarketCompetitor | null =>
    [...rankedCompetitors(data)].sort((a, b) => n(b.organicKeywords) - n(a.organicKeywords))[0] ?? null;

/** Share (0–1) of the searches you both appear for where they are on page one. */
export const sharedPageOneShare = (c: MarketCompetitor): number | null =>
    has(c.sharedTop10) && n(c.sharedKeywords) > 0 ? clamp(c.sharedTop10 / (c.sharedKeywords as number), 0, 1) : null;

export type RivalSize = 'bigger' | 'level' | 'smaller';

/** Bigger, level or smaller than you, by searches ranked for — ±25% counts as level. */
export function rivalSize(c: MarketCompetitor, you: You): { size: RivalSize; ratio: number } | null {
    const r = ratioOf(c.organicKeywords, you.keywords);
    if (r == null) return null;
    if (r >= 1.25) return { size: 'bigger', ratio: r };
    if (r <= 0.8) return { size: 'smaller', ratio: 1 / r };
    return { size: 'level', ratio: r };
}

export interface MapPoint {
    domain: string;
    keywords: number;
    visits: number;
    shared: number;
    self: boolean;
}

/**
 * The positioning map: every rival and the vendor on the same two axes —
 * how many searches each is found for, and the visits those bring. Bubble
 * size is how much of the vendor's ground each rival covers.
 */
export function positioningPoints(data: Pick<MarketData, 'target' | 'overview' | 'competitors'>): MapPoint[] {
    const you = youOf(data);
    const rivals = rankedCompetitors(data)
        .filter((c) => n(c.organicKeywords) > 0 || n(c.estimatedMonthlyVisits) > 0)
        .map((c) => ({
            domain: cleanDomain(c.domain),
            keywords: n(c.organicKeywords),
            visits: n(c.estimatedMonthlyVisits),
            shared: n(c.sharedKeywords),
            self: false,
        }));
    if (you.keywords > 0 || you.visits > 0) {
        // Your bubble is sized like the largest overlap, so it is never the smallest thing on the map.
        const shared = Math.max(1, ...rivals.map((r) => r.shared));
        rivals.push({ domain: you.domain, keywords: you.keywords, visits: you.visits, shared, self: true });
    }
    return rivals;
}

/** Who takes the visits on the searches you share: each rival's share of the total. */
export function sharedVisitShare(data: Pick<MarketData, 'competitors'>): { domain: string; visits: number; share: number }[] {
    const rows = rankedCompetitors(data)
        .filter((c) => has(c.sharedVisits))
        .map((c) => ({ domain: cleanDomain(c.domain), visits: n(c.sharedVisits) }));
    const total = rows.reduce((s, r) => s + r.visits, 0);
    if (!total) return [];
    return rows.map((r) => ({ ...r, share: r.visits / total })).sort((a, b) => b.visits - a.visits);
}

/* ─────────────────────────────── gaps ─────────────────────────────── */

/**
 * How much each kind of search is worth to a shop. Someone ready to buy is
 * worth the most; someone searching a brand by name mostly wants that brand.
 */
export const INTENT_WEIGHT: Record<IntentKey, number> = {
    transactional: 1,
    commercial: 0.9,
    informational: 0.6,
    navigational: 0.35,
    unknown: 0.6,
};

const fold = (s: string) =>
    s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();

/**
 * Whether a search is for the rival's own name ("carrelage design lyon" for
 * carrelage-design.fr). The vendor is not going to win those, and a list that
 * put them first would send them after someone else's customers.
 */
export function isBrandSearch(keyword: string | null | undefined, domain: string | null | undefined): boolean {
    const stem = cleanDomain(domain).split('.')[0] ?? '';
    if (stem.length < 4 || !keyword) return false;
    const k = fold(keyword);
    const spaced = fold(stem).replace(/[-_]+/g, ' ');
    const joined = spaced.replace(/\s+/g, '');
    return k.includes(spaced) || k.replace(/\s+/g, '').includes(joined);
}

/**
 * The opportunity in a search a rival wins and the vendor does not, 0–100:
 * how many people make it, how hard page one is, and whether they are looking
 * to buy. A brand search is heavily discounted.
 */
export function gapScore(k: { searchVolume?: number | null; difficulty?: number | null; intent?: string | null }, opts: { brand?: boolean } = {}): number {
    const intent = (k.intent && k.intent in INTENT_WEIGHT ? k.intent : 'unknown') as IntentKey;
    const raw = 100 * volumeWeight(k.searchVolume) * (0.35 + 0.65 * easeOf(k.difficulty)) * INTENT_WEIGHT[intent];
    return Math.round(opts.brand ? raw * 0.2 : raw);
}

export type ScoreLevel = 'high' | 'medium' | 'low';
export const scoreLevel = (score: number): ScoreLevel => (score >= 55 ? 'high' : score >= 30 ? 'medium' : 'low');

export interface GapRival {
    rival: string;
    position: number | null;
    url: string | null;
    visits: number;
}

export interface GapRow {
    keyword: string;
    searchVolume: number;
    difficulty: number | null;
    intent: MarketIntent | null;
    cpc: number | null;
    trend: number[];
    rivals: GapRival[];
    brand: boolean;
    score: number;
}

/**
 * Every rival's gap as one list, one row per search.
 *
 * A search two rivals both win is one opportunity with more proof behind it,
 * not two rows — so they are merged and the score is raised a little for each
 * extra rival, capped at 100.
 */
export function mergeGaps(gaps: MarketKeywordGap[] | undefined): GapRow[] {
    const rows = new Map<string, GapRow>();
    for (const gap of gaps ?? []) {
        for (const k of gap.summary?.keywords ?? []) {
            if (!k.keyword) continue;
            const id = fold(k.keyword).trim();
            const rival: GapRival = { rival: cleanDomain(gap.rival), position: k.theirPosition ?? null, url: k.theirUrl ?? null, visits: n(k.theirVisits) };
            const row = rows.get(id);
            if (row) {
                row.rivals.push(rival);
                // A search for one rival's name stays a brand search whoever else ranks for it.
                row.brand = row.brand || isBrandSearch(k.keyword, gap.rival);
                if (row.difficulty == null && has(k.difficulty)) row.difficulty = k.difficulty;
                if (!row.intent && k.intent) row.intent = k.intent;
                if (row.cpc == null && has(k.cpc)) row.cpc = k.cpc;
                if (!row.trend.length && k.trend?.length) row.trend = k.trend;
                row.searchVolume = Math.max(row.searchVolume, n(k.searchVolume));
            } else {
                rows.set(id, {
                    keyword: k.keyword,
                    searchVolume: n(k.searchVolume),
                    difficulty: has(k.difficulty) ? k.difficulty : null,
                    intent: k.intent ?? null,
                    cpc: has(k.cpc) ? k.cpc : null,
                    trend: k.trend ?? [],
                    rivals: [rival],
                    brand: isBrandSearch(k.keyword, gap.rival),
                    score: 0,
                });
            }
        }
    }
    const out = [...rows.values()];
    for (const row of out) {
        row.rivals.sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
        const base = gapScore(row, { brand: row.brand });
        row.score = Math.min(100, Math.round(base * (1 + 0.1 * (row.rivals.length - 1))));
    }
    return out.sort((a, b) => b.score - a.score || b.searchVolume - a.searchVolume);
}

/** Which rival holds the most of the missing demand. */
export function gapOwners(rows: GapRow[]): { rival: string; keywords: number; volume: number }[] {
    const owners = new Map<string, { rival: string; keywords: number; volume: number }>();
    for (const row of rows) {
        for (const r of row.rivals) {
            const o = owners.get(r.rival) ?? { rival: r.rival, keywords: 0, volume: 0 };
            o.keywords += 1;
            o.volume += row.searchVolume;
            owners.set(r.rival, o);
        }
    }
    return [...owners.values()].sort((a, b) => b.volume - a.volume);
}

/* ─────────────────────────────── profile ─────────────────────────────── */

export type ListingField = 'category' | 'address' | 'phone' | 'website' | 'hours' | 'photos';
export const LISTING_FIELDS: ListingField[] = ['category', 'address', 'phone', 'website', 'hours', 'photos'];

/** Which of the details a customer looks for the listing actually has. */
export function listingCompleteness(b: MarketData['business']): { filled: ListingField[]; missing: ListingField[] } | null {
    if (!b || b.notFound) return null;
    const present: Record<ListingField, boolean> = {
        category: !!b.category,
        address: !!b.address,
        phone: !!b.phone,
        website: !!b.website,
        hours: !!b.workTime,
        photos: n(b.photos) > 0,
    };
    return {
        filled: LISTING_FIELDS.filter((f) => present[f]),
        missing: LISTING_FIELDS.filter((f) => !present[f]),
    };
}

/** Stars by count, five first; null when the listing has no breakdown. */
export function ratingRows(breakdown: Record<string, number> | null | undefined): { star: number; count: number }[] | null {
    if (!breakdown) return null;
    const rows = [5, 4, 3, 2, 1].map((star) => ({ star, count: n(breakdown[String(star)]) }));
    return rows.some((r) => r.count > 0) ? rows : null;
}

/** The lowest-rated branch that has been rated, when there are several. */
export function worstBranch(b: MarketData['business']) {
    const rated = (b?.locations ?? []).filter((l) => has(l.rating));
    if (rated.length < 2) return null;
    return [...rated].sort((x, y) => (x.rating as number) - (y.rating as number))[0];
}
